import type { FleetMetricAggregate, FleetMetricDimension, FleetMetricGroup } from '@immo/api-contract';
import { costOf, sumClasses, tokensByClass, type TokenClass } from './pricing';

/** What the page draws: all four classes summed, or one class alone. */
export type ClassChoice = 'all' | TokenClass;

export type MetricsView = 'machine' | 'agent' | 'model';

export const METRICS_VIEWS: ReadonlyArray<MetricsView> = ['machine', 'agent', 'model'];

/** Each view is only a grouping: `[panel, row]`. */
export const VIEW_GROUPING: Readonly<Record<MetricsView, readonly [FleetMetricDimension, FleetMetricDimension]>> = {
  machine: ['host', 'profile'],
  agent: ['profile', 'host'],
  model: ['model', 'profile'],
};

export const EMPTY_AGGREGATE: FleetMetricAggregate = {
  runs: 0,
  unparsedRuns: 0,
  durationMs: 0,
  uncachedInputTokens: 0,
  cacheReadInputTokens: 0,
  cacheWriteInputTokens: 0,
  outputTokens: 0,
};

export function addAggregates(a: FleetMetricAggregate, b: FleetMetricAggregate): FleetMetricAggregate {
  return {
    runs: a.runs + b.runs,
    unparsedRuns: a.unparsedRuns + b.unparsedRuns,
    durationMs: a.durationMs + b.durationMs,
    uncachedInputTokens: a.uncachedInputTokens + b.uncachedInputTokens,
    cacheReadInputTokens: a.cacheReadInputTokens + b.cacheReadInputTokens,
    cacheWriteInputTokens: a.cacheWriteInputTokens + b.cacheWriteInputTokens,
    outputTokens: a.outputTokens + b.outputTokens,
  };
}

export function classValue(aggregate: FleetMetricAggregate | null, choice: ClassChoice): number {
  if (aggregate === null) return 0;

  const tokens = tokensByClass(aggregate);

  return choice === 'all' ? sumClasses(tokens) : tokens[choice];
}

/** Runs happened and not one of them has readable usage: the tokens are unknown — `—`, never `0`. */
export function isUnknown(aggregate: FleetMetricAggregate | null): boolean {
  return aggregate !== null && aggregate.runs > 0 && aggregate.unparsedRuns === aggregate.runs;
}

/** A sum a run without readable usage fell into is a lower bound — `≈`. */
export function isApproximate(aggregate: FleetMetricAggregate | null): boolean {
  return aggregate !== null && aggregate.unparsedRuns > 0;
}

export function parsedRuns(aggregate: FleetMetricAggregate): number {
  return aggregate.runs - aggregate.unparsedRuns;
}

/** A round axis maximum: 1 · 2 · 2.5 · 5 · 10 × 10ⁿ. */
export function niceMax(value: number): number {
  if (value <= 0) return 1;

  const magnitude = 10 ** Math.floor(Math.log10(value));
  const fraction = value / magnitude;
  const step = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10;

  return step * magnitude;
}

/** Change against the period before, rounded to whole percent; `new` when there was nothing to compare to. */
export type Delta = { readonly kind: 'percent'; readonly value: number } | { readonly kind: 'new' };

export function delta(current: number, previous: number): Delta {
  if (previous === 0) return current === 0 ? { kind: 'percent', value: 0 } : { kind: 'new' };

  return { kind: 'percent', value: Math.round(((current - previous) / previous) * 100) };
}

export function keyOf(group: FleetMetricGroup, dimension: FleetMetricDimension): string {
  return group.key[dimension] ?? '';
}

/** The largest bucket value across every series, for one shared scale. */
export function seriesMax(groups: ReadonlyArray<FleetMetricGroup>, choice: ClassChoice): number {
  return groups.reduce(
    (max, group) => group.series.reduce((inner, aggregate) => Math.max(inner, classValue(aggregate, choice)), max),
    0,
  );
}

/**
 * Euro per value of `dimension`, from groups keyed by it and by `model` — the
 * price depends on the model, so money can only be summed once the model is known.
 */
export function costBy(groups: ReadonlyArray<FleetMetricGroup>, dimension: FleetMetricDimension): Map<string, number> {
  const costs = new Map<string, number>();

  for (const group of groups) {
    const key = keyOf(group, dimension);
    const cost = costOf(keyOf(group, 'model'), tokensByClass(group.total));

    costs.set(key, (costs.get(key) ?? 0) + cost);
  }

  return costs;
}

export function sumAggregates(aggregates: ReadonlyArray<FleetMetricAggregate>): FleetMetricAggregate {
  return aggregates.reduce(addAggregates, EMPTY_AGGREGATE);
}
